import {
  ActionIcon,
  Button,
  ButtonProps,
  FileButton as MantineFileButton,
  FileButtonProps,
  Flex,
  Text,
} from '@mantine/core';
import {FC, ReactNode, useContext} from 'react';
import {FieldAdapter} from '../field-factory';
import {PrimaryColorCssVar} from '../../styles/default-theme';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faExternalLink,
  faFileUpload,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import {ReadOnlyContext} from '../readonly-context';
import classes from './file-button.module.css';

export type $FileButtonAdapterProps = Omit<
  FileButtonProps,
  'children' | 'onChange'
> & {
  buttonText?: string;
  loading?: boolean;
  renderPreviewComponent?: (uri: string) => ReactNode;
  onFileClear?: () => void;
  // This field expects a URI string returned from the onChange.
  onChange: (file: File | null) => Promise<string | undefined>;
  buttonStyles?: ButtonProps['styles'];
};

export const $FileButtonAdapter: FC<FieldAdapter<$FileButtonAdapterProps>> = (
  props,
) => {
  const {
    inputProps: {
      onChange,
      disabled,
      buttonText = 'Upload File',
      onFileClear,
      renderPreviewComponent,
      buttonStyles,
      loading = false,
      ...inputProps
    },
    control,
  } = props;

  const {
    field: {value, onChange: fieldOnChange, ...fieldProps},
  } = control;

  const {readOnly} = useContext(ReadOnlyContext);

  const _onChange: FileButtonProps['onChange'] = async (file) => {
    const uri = await onChange(file);
    fieldOnChange(uri);
  };

  const _onFileClear = () => {
    fieldOnChange(null);
    onFileClear?.();
  };

  return (
    <Flex direction={'column'} gap={5}>
      {!value ? (
        <MantineFileButton
          multiple={false}
          disabled={disabled || readOnly}
          onChange={_onChange}
          {...inputProps}
          {...fieldProps}
        >
          {(props) => (
            <Button
              styles={buttonStyles}
              variant='outline'
              loading={loading}
              {...props}
              radius={18}
              leftSection={<FontAwesomeIcon icon={faFileUpload} />}
            >
              {buttonText}
            </Button>
          )}
        </MantineFileButton>
      ) : (
        <Flex
          direction={'row'}
          gap={20}
          align={'center'}
          justify={'space-between'}
          maw={'100%'}
        >
          {renderPreviewComponent?.(value) || (
            <Flex
              direction={'row'}
              gap={5}
              align={'center'}
              className={classes.maxWidth}
            >
              <Text
                component='a'
                href={value}
                target='_blank'
                rel='noreferrer'
                c={PrimaryColorCssVar}
                truncate
              >
                {extractFileName(value)} &nbsp;
                <FontAwesomeIcon icon={faExternalLink} fontSize={'14px'} />
              </Text>
            </Flex>
          )}
          <ActionIcon onClick={_onFileClear}>
            <FontAwesomeIcon icon={faTrashAlt} color={'red'} />
          </ActionIcon>
        </Flex>
      )}
    </Flex>
  );
};

export function extractFileName(uri: string): string {
  const parts = uri.split('/');
  const fileNameWithGuid = parts[parts.length - 1];

  const lastDotIndex = fileNameWithGuid.lastIndexOf('.');

  const baseName = fileNameWithGuid.substring(0, lastDotIndex);

  const extension = fileNameWithGuid.substring(lastDotIndex);

  const firstDashBeforeGuid = baseName.indexOf('-');

  const fileNameWithoutGuid = baseName.substring(0, firstDashBeforeGuid);
  return `${fileNameWithoutGuid}${extension}`;
}

export const MAX_LOGO_FILE_BYTE_SIZE = 10000000;
export const MAX_ICON_FILE_BYTE_SIZE = 10000000;
export const MAX_PROFILE_FILE_BYTE_SIZE = 5000000;
export const MAX_NEWS_FEED_ATTACHMENT_FILE_BYTE_SIZE = 10000000;
